import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { IconZzz, IconBolt, IconArrowRight } from '@tabler/icons-react';
import { useNavTheme } from 'contexts/NavThemeContext';
import useSubscriptions from 'hooks/useSubscriptions';

// Centralized threshold - tasks won't execute below this value
export const LOW_BALANCE_THRESHOLD = 15;

// Hook to check if balance is low
export function useIsLowBalance() {
  const { currentBalance, isSuccess } = useSubscriptions();
  // Only report low balance after data has loaded to prevent false positives
  if (!isSuccess) return false;
  return currentBalance < LOW_BALANCE_THRESHOLD;
}

interface LowBalanceWarningProps {
  /** Context-specific message describing what won't work */
  contextMessage?: string;
}

/**
 * Page-level warning banner for low ACU balance
 * Shows when balance is below LOW_BALANCE_THRESHOLD
 */
export default function LowBalanceWarning({
  contextMessage = "Tasks and workers won't execute until you add more compute units."
}: LowBalanceWarningProps) {
  const navigate = useNavigate();
  const { mode, tokens } = useNavTheme();
  const { currentBalance, plan, isSuccess } = useSubscriptions();
  const isLight = mode === 'light';

  // Don't show until subscription data has loaded to prevent flash of warning
  if (!isSuccess) return null;

  const isLowBalance = currentBalance < LOW_BALANCE_THRESHOLD;
  const isFreePlan = !plan || plan.code === 'free';

  if (!isLowBalance) return null;

  const handleClick = () => {
    if (isFreePlan) {
      navigate('/settings?tab=subscription');
    } else {
      navigate('/settings?tab=buy-acu');
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        mb: 4,
        p: 0,
        borderRadius: '14px',
        overflow: 'hidden',
        cursor: 'pointer',
        background: isLight
          ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.04) 0%, rgba(220, 38, 38, 0.08) 100%)'
          : 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(185, 28, 28, 0.14) 100%)',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        transition: 'all 0.2s ease',
        '&:hover': {
          background: isLight
            ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.12) 100%)'
            : 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(185, 28, 28, 0.18) 100%)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box
        sx={{
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(239, 68, 68, 0.12)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            flexShrink: 0,
          }}
        >
          <IconZzz size={24} style={{ color: '#ef4444' }} />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 700,
              color: '#ef4444',
              fontFamily: '"Satoshi", sans-serif',
              mb: 0.5,
            }}
          >
            AI Workers Paused
          </Typography>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: tokens.textSecondary,
              lineHeight: 1.5,
            }}
          >
            Your compute balance is below{' '}
            <Box
              component="span"
              sx={{
                fontWeight: 700,
                fontFamily: '"JetBrains Mono", monospace',
                color: '#ef4444',
              }}
            >
              {LOW_BALANCE_THRESHOLD} ACU
            </Box>
            . {contextMessage}
          </Typography>
        </Box>

        {/* CTA */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: '10px',
            background: '#ef4444',
            color: '#ffffff',
            flexShrink: 0,
            transition: 'all 0.15s ease',
            '&:hover': {
              background: '#dc2626',
            },
          }}
        >
          <IconBolt size={16} />
          <Typography
            sx={{
              fontSize: '0.8rem',
              fontWeight: 700,
              fontFamily: '"Satoshi", sans-serif',
              whiteSpace: 'nowrap',
            }}
          >
            {isFreePlan ? 'Upgrade Plan' : 'Add Credits'}
          </Typography>
          <IconArrowRight size={14} />
        </Box>
      </Box>
    </Box>
  );
}
